/**
 * Sync utilities: batching, throttling, serialization, size checks.
 * Used for whiteboard stroke sync over Daily app messages.
 */

import {
  MESSAGE_TYPES,
  MESSAGE_SIZE_LIMIT_BYTES,
  BATCH_INTERVAL_MS,
  THROTTLE_MAX_PER_SECOND,
  VALID_SURFACES,
} from './constants';

/**
 * Get byte size of a serializable object (for Daily 4KB limit check).
 * Prefers TextEncoder (accurate UTF-8) when available; falls back to Blob/Buffer.
 * @param {object} obj - Object to measure
 * @returns {number} Size in bytes, or 0 for invalid input
 */
export function getPayloadSizeBytes(obj) {
  if (obj == null) return 0;
  try {
    const str = JSON.stringify(obj);
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(str).length;
    }
    if (typeof Blob !== 'undefined') {
      return new Blob([str]).size;
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.byteLength(str, 'utf8');
    }
    return str.length * 2;
  } catch {
    return 0;
  }
}

/**
 * Check if payload is within Daily's message size limit.
 * @param {object} obj - Serialized payload
 * @returns {boolean} False for null/undefined or oversized payload
 */
export function isWithinSizeLimit(obj) {
  if (obj == null) return false;
  return getPayloadSizeBytes(obj) <= MESSAGE_SIZE_LIMIT_BYTES;
}

/**
 * Serialize a stroke batch for sending.
 * @param {object} params
 * @param {string} params.studentId - Target student
 * @param {string} params.senderId - Who drew
 * @param {string} params.strokeId - Stroke identifier
 * @param {Array<[number, number]>} params.points - Points in logical coords
 * @param {number} params.timestamp - Unix ms
 * @param {string} params.surface - 'whiteboard' | 'lesson'
 * @param {string} [params.color] - Hex stroke color (e.g. #2563eb)
 * @returns {object | null} Serialized payload or null if invalid
 */
export function serializeStroke({ studentId, senderId, strokeId, points, timestamp, surface, color }) {
  if (
    !studentId ||
    !senderId ||
    !strokeId ||
    !Array.isArray(points) ||
    points.length < 1 ||
    typeof timestamp !== 'number' ||
    !Number.isFinite(timestamp) ||
    !VALID_SURFACES.includes(surface)
  ) {
    return null;
  }

  const pointsClean = points
    .map((p) => {
      if (!Array.isArray(p) || p.length < 2) return null;
      const x = Number(p[0]);
      const y = Number(p[1]);
      return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
    })
    .filter(Boolean);

  if (pointsClean.length === 0) return null;

  const payload = {
    type: MESSAGE_TYPES.STROKE,
    studentId,
    senderId,
    strokeId,
    points: pointsClean,
    timestamp,
    surface,
    ...(color && typeof color === 'string' ? { color } : {}),
  };

  return payload;
}

/**
 * Serialize an erase action.
 * @param {object} params
 * @param {string} params.studentId - Target student
 * @param {string} params.senderId - Who erased
 * @param {string[]} params.strokeIds - IDs of strokes to remove
 * @param {string} params.surface - 'whiteboard' | 'lesson'
 * @returns {object | null}
 */
export function serializeErase({ studentId, senderId, strokeIds, surface }) {
  if (
    !studentId ||
    !senderId ||
    !Array.isArray(strokeIds) ||
    strokeIds.length < 1 ||
    !VALID_SURFACES.includes(surface)
  ) {
    return null;
  }

  const ids = strokeIds.filter((id) => typeof id === 'string' && id.length > 0);
  if (ids.length === 0) return null;

  return {
    type: MESSAGE_TYPES.ERASE,
    studentId,
    senderId,
    strokeIds: ids,
    surface,
  };
}

/**
 * Serialize a clear action.
 * @param {object} params
 * @param {string} params.studentId - Target student
 * @param {string} params.senderId - Who cleared
 * @param {string} [params.surface] - 'whiteboard' | 'lesson'; omit to clear both
 * @returns {object | null}
 */
export function serializeClear({ studentId, senderId, surface }) {
  if (!studentId || !senderId) return null;
  if (surface != null && !VALID_SURFACES.includes(surface)) return null;

  return {
    type: MESSAGE_TYPES.CLEAR,
    studentId,
    senderId,
    ...(surface != null && surface !== '' ? { surface } : {}),
  };
}

/**
 * Serialize a layout sync message (tutor drags divider → child applies same ratio).
 * @param {object} params
 * @param {string} params.studentId - Target student
 * @param {string} params.senderId - Who sent (tutor)
 * @param {number} params.whiteboardRatio - 0–1, whiteboard width / total width
 * @returns {object | null}
 */
export function serializeLayout({ studentId, senderId, whiteboardRatio }) {
  if (!studentId || !senderId || typeof whiteboardRatio !== 'number' || !Number.isFinite(whiteboardRatio)) {
    return null;
  }
  return {
    type: MESSAGE_TYPES.LAYOUT,
    studentId,
    senderId,
    whiteboardRatio: Math.max(0.05, Math.min(0.95, whiteboardRatio)),
  };
}

/**
 * Serialize whiteboard activate message (tutor opens/closes whiteboard → student follows).
 * @param {object} params
 * @param {string} params.studentId - Target student
 * @param {string} params.senderId - Who sent (tutor)
 * @param {boolean} params.active - Whether whiteboard is open
 * @returns {object | null}
 */
export function serializeActivate({ studentId, senderId, active }) {
  if (!studentId || !senderId || typeof active !== 'boolean') return null;
  return {
    type: MESSAGE_TYPES.ACTIVATE,
    studentId,
    senderId,
    active,
  };
}

/**
 * Serialize remote cursor position (so the other side can show pen/eraser icon).
 * @param {object} params
 * @param {string} params.studentId - Target student
 * @param {string} params.senderId - Who is moving the cursor
 * @param {string} params.surface - 'lesson' | 'whiteboard'
 * @param {number} params.x - Logical x
 * @param {number} params.y - Logical y
 * @param {string} params.tool - 'pen' | 'eraser'
 * @param {boolean} [params.visible] - If false, hide cursor on the other side (e.g. pointer left)
 * @returns {object | null}
 */
export function serializeCursor({ studentId, senderId, surface, x, y, tool, visible = true }) {
  if (!studentId || !senderId || !VALID_SURFACES.includes(surface)) return null;
  const px = Number(x);
  const py = Number(y);
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  if (tool !== 'pen' && tool !== 'eraser') return null;
  return {
    type: MESSAGE_TYPES.CURSOR,
    studentId,
    senderId,
    surface,
    x: px,
    y: py,
    tool,
    visible: !!visible,
  };
}

/**
 * Create a batch queue that collects points and flushes on a timer.
 * Non-blocking; uses setTimeout.
 * @param {object} config
 * @param {number} [config.intervalMs] - Flush interval
 * @param {function} config.onFlush - Called with { strokeId, surface, senderId, points }
 */
export function createBatchQueue({ intervalMs = BATCH_INTERVAL_MS, onFlush }) {
  let buffer = [];
  let meta = null;
  let timerId = null;

  function flush() {
    if (timerId != null) {
      clearTimeout(timerId);
      timerId = null;
    }
    if (buffer.length > 0 && meta) {
      const payload = { ...meta, points: [...buffer] };
      buffer = [];
      try {
        onFlush(payload);
      } catch (err) {
        if (typeof console !== 'undefined' && console.error) {
          console.error('[whiteboard] Batch flush error:', err);
        }
      }
    }
  }

  function schedule() {
    if (timerId != null) return;
    timerId = setTimeout(flush, Math.max(1, intervalMs));
  }

  return {
    add(strokeId, surface, senderId, x, y) {
      if (!strokeId || !VALID_SURFACES.includes(surface) || !senderId) return;
      const px = Number(x);
      const py = Number(y);
      if (!Number.isFinite(px) || !Number.isFinite(py)) return;

      if (meta == null) {
        meta = { strokeId, surface, senderId };
      } else if (meta.strokeId !== strokeId || meta.surface !== surface) {
        flush();
        meta = { strokeId, surface, senderId };
      }

      buffer.push([px, py]);
      schedule();
    },

    flush() {
      flush();
      meta = null;
    },

    cancel() {
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      buffer = [];
      meta = null;
    },
  };
}

/**
 * Create a throttled function that limits invocations per second.
 * Returns an object with invoke() and cancel() for cleanup on unmount.
 * @param {function} fn - Function to throttle (no args expected)
 * @param {object} [config]
 * @param {number} [config.maxPerSecond] - Max invocations per second
 * @returns {{ invoke: function, cancel: function }}
 */
export function createThrottle(fn, { maxPerSecond = THROTTLE_MAX_PER_SECOND } = {}) {
  const minIntervalMs = 1000 / Math.max(1, maxPerSecond);
  let lastCall = 0;
  let scheduledId = null;

  function run() {
    scheduledId = null;
    lastCall = Date.now();
    try {
      fn();
    } catch (err) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[whiteboard] Throttled fn error:', err);
      }
    }
  }

  return {
    invoke() {
      const now = Date.now();
      const elapsed = now - lastCall;

      if (elapsed >= minIntervalMs || lastCall === 0) {
        run();
      } else if (scheduledId == null) {
        scheduledId = setTimeout(run, minIntervalMs - elapsed);
      }
    },
    cancel() {
      if (scheduledId != null) {
        clearTimeout(scheduledId);
        scheduledId = null;
      }
    },
  };
}
